import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import api from "../../../api/api";

export default function WorkItemsTab({ projectId }) {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [projectType, setProjectType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  // ⭐ CHANGE
  // Added work_item_code to allow manual Work Item ID entry
  const [form, setForm] = useState({
    work_item_code: "", 
    title: "",
    description: "",
    work_type: "TASK",
    priority: "MEDIUM",
    sprint_id: "",
  });

  useEffect(() => {
    loadProject();
    loadItems();
  }, [projectId]);

  useEffect(() => {
    if (projectType === "DEVELOPMENT") {
      loadSprints();
    }
  }, [projectType]);

  const loadProject = async () => {
    try {
      const res = await api.get(`/projects/${projectId}`);
      setProjectType(res.data.project_type);
    } catch (err) {
      console.error("Error loading project", err);
    }
  };

  const loadItems = async () => {
    try {
      const res = await api.get(`/work-items/?project_id=${projectId}`);
      setItems(res.data);
    } catch (err) {
      console.error("Error loading work items", err);
    } finally {
      setLoading(false);
    }
  };

  const loadSprints = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/sprints`);
      setSprints(res.data);
    } catch (err) {
      console.error("Error loading sprints", err);
    }
  };

  const createItem = async () => {
    if (!form.title) return;

    const payload = {
      // ⭐ CHANGE
      // Send manual work item code to backend
      work_item_code: form.work_item_code,

      title: form.title,
      description: form.description,
      work_type: form.work_type,
      priority: form.priority,
      project_id: projectId,
      sprint_id:
        projectType === "DEVELOPMENT"
          ? form.sprint_id || null
          : null,
    };

    try {
      await api.post("/work-items/", payload);

      setOpen(false);

      // ⭐ CHANGE
      // Reset work_item_code after create
      setForm({
        work_item_code: "",
        title: "",
        description: "",
        work_type: "TASK",
        priority: "MEDIUM",
        sprint_id: "",
      });

      loadItems();
    } catch (err) {
      console.error("Error creating item", err);
    }
  };

  const statusColor = (status) => {
    if (status === "OPEN") return "primary";
    if (status === "IN_PROGRESS") return "warning";
    if (status === "DONE") return "success";
    return "default";
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Work Items</Typography>
        <Button variant="contained" onClick={() => setOpen(true)}>
          New Task
        </Button>
      </Box>

      {loading && (
        <Typography color="text.secondary">
          Loading work items...
        </Typography>
      )}

      {!loading && items.length === 0 && (
        <Typography color="text.secondary">
          No work items yet.
        </Typography>
      )}

      {items.map((item) => (
        <Card
          key={item.id}
          sx={{
            mb: 2,
            cursor: "pointer",
            transition: "0.2s",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: 3,
            },
          }}
          onClick={() => navigate(`/work-items/${item.id}`)}
        >
          <CardContent>

            {/* ⭐ CHANGE
               Show Work Item ID before title if available
            */}
            <Typography fontWeight="bold">
              {item.work_item_code && (
                <span style={{ color: "#d32f2f", marginRight: 6 }}>
                  {item.work_item_code}
                </span>
              )}
              {item.title}
            </Typography>

            {item.description && (
              <Typography variant="body2" color="text.secondary" mt={1}>
                {item.description}
              </Typography>
            )}

            <Box mt={1} display="flex" gap={1} flexWrap="wrap">
              <Chip label={item.work_type} size="small" />
              <Chip label={item.priority} size="small" />
              <Chip
                label={item.status}
                size="small"
                color={statusColor(item.status)}
              />

              {item.sprint_id && (
                <Chip label={`Sprint ${item.sprint_id}`} size="small" />
              )}

              {item.assignee && (
                <Chip
                  label={`👤 ${item.assignee.name}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </CardContent>
        </Card>
      ))}

      {/* Create Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
        <DialogTitle>Create Task</DialogTitle>

        <DialogContent>

          {/* ⭐ CHANGE
             Manual Work Item ID input
          */}
          <TextField
            fullWidth
            label="Work Item ID"
            margin="normal"
            value={form.work_item_code}
            onChange={(e) =>
              setForm({ ...form, work_item_code: e.target.value })
            }
          />

          <TextField
            fullWidth
            label="Title"
            margin="normal"
            value={form.title}
            onChange={(e) =>
              setForm({ ...form, title: e.target.value })
            }
          />

          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            rows={3}
            value={form.description}
            onChange={(e) =>
              setForm({ ...form, description: e.target.value })
            }
          />

          {/* Sprint ONLY for DEVELOPMENT */}
          {projectType === "DEVELOPMENT" && (
            <TextField
              select
              fullWidth
              label="Sprint"
              margin="normal"
              value={form.sprint_id}
              onChange={(e) =>
                setForm({ ...form, sprint_id: e.target.value })
              }
            >
              {sprints.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            select
            fullWidth
            label="Type"
            margin="normal"
            value={form.work_type}
            onChange={(e) =>
              setForm({ ...form, work_type: e.target.value })
            }
          >
            <MenuItem value="TASK">Task</MenuItem>
            <MenuItem value="BUG">Bug</MenuItem>
            <MenuItem value="TICKET">Ticket</MenuItem>
          </TextField>

          <TextField
            select
            fullWidth
            label="Priority"
            margin="normal"
            value={form.priority}
            onChange={(e) =>
              setForm({ ...form, priority: e.target.value })
            }
          >
            <MenuItem value="LOW">Low</MenuItem>
            <MenuItem value="MEDIUM">Medium</MenuItem>
            <MenuItem value="HIGH">High</MenuItem>
          </TextField>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createItem}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}